import type { CSSProperties, ReactNode } from "react";

interface Props {
  kind?: string;
  tone?: "light" | "dark";
  radius?: number;
  style?: CSSProperties;
  children?: ReactNode;
  /** Optional image URL - when set, the photo replaces the striped placeholder. */
  src?: string;
}

/**
 * Striped editorial placeholder used by Atelier (hero) and Spectrum
 * (film still). Renders a deliberate "drop a photo here" pattern so the
 * scheme reads finished even before the operator adds imagery. When `src`
 * is supplied the photo takes over with `background-size: cover`.
 */
export function ImageSlot({
  kind = "photo",
  tone = "light",
  radius = 0,
  style,
  children,
  src,
}: Props) {
  const stripedBg =
    tone === "dark"
      ? "repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 14px, rgba(255,255,255,0.08) 14px 28px)"
      : "repeating-linear-gradient(135deg, rgba(0,0,0,0.045) 0 14px, rgba(0,0,0,0.085) 14px 28px)";
  const border =
    tone === "dark" ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.12)";
  const ink = tone === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)";
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: radius,
        background: src ? "#000" : stripedBg,
        backgroundImage: src
          ? `url(${src})`
          : tone === "dark"
            ? "repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 14px, rgba(255,255,255,0.08) 14px 28px)"
            : "repeating-linear-gradient(135deg, rgba(0,0,0,0.045) 0 14px, rgba(0,0,0,0.085) 14px 28px)",
        backgroundSize: src ? "cover" : undefined,
        backgroundPosition: src ? "center" : undefined,
        boxShadow: `inset 0 0 0 1px ${border}`,
        display: "flex",
        alignItems: "flex-end",
        ...style,
      }}
    >
      {!src && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 14,
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
            fontSize: 11,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: ink,
          }}
        >
          {kind}
        </div>
      )}
      {children}
    </div>
  );
}
